/**
 * Request-flow editor dialog stack.
 * Owns the modal surfaces for missing-detail completion, evidence review, rejected suggestions,
 * medical opinion actions, required-field controls, and provider signing.
 */
import React from 'react';
import { Alert, Button, Col, Form, Image, Modal, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faFileSignature,
  faLightbulb,
  faSignature,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';

import MoEvidenceModal from '../MoEvidenceModal';
import DbqRemarksEditorModal from '../DbqRemarksEditorModal';
import PageContentModal from '../PageContentModal';
import {
  buildInlineMissingInputStateKey,
  formatInlineSuggestionDateTime,
  getInlineGenerationFailureMessage,
  getInlineSuggestionDisplayText,
  getInlineSuggestionReviewerName,
  MISSING_USER_INPUT_TOKEN,
} from './userDbqEditorRequestFlowUtils';

const UserDbqEditorRequestFlowDialogs = ({
  inlineMissingDetailSuggestion,
  closeInlineMissingDetailModal,
  inlineMissingDetailInputs,
  inlineMissingInputValues,
  inlineMissingInputSearches,
  isInlineMissingDetailSaving,
  handleSearchInlineMissingInputValue,
  handleInlineMissingInputValueChange,
  handleApplyInlineMissingInputValue,
  inlineMissingDetailDraftText,
  setInlineMissingDetailDraftText,
  handleSaveInlineMissingDetail,
  inlineRejectedSuggestionsFieldId,
  closeInlineRejectedSuggestionsModal,
  getFieldLabelByKey,
  inlineRejectedSuggestions,
  inlineEvidenceSuggestion,
  closeInlineEvidenceModal,
  inlineEvidenceFieldId,
  inlineEvidenceRefs,
  openInlineEvidencePage,
  inlineEvidenceTranscriptRefs,
  inlineEvidencePage,
  closeInlineEvidencePage,
  userDbq,
  inlineGenerationFailureNotice,
  closeInlineGenerationFailureModal,
  inlineGenerationFailureFieldId,
  canManageMedicalOpinion,
  showMoEvidenceModal,
  setShowMoEvidenceModal,
  userDbqId,
  autoMedicalOpinionData,
  isManagedMedicalOpinionRemarks,
  showRemarksEditorModal,
  setShowRemarksEditorModal,
  setRemarksEditorSubmitMode,
  remarksEditorText,
  setRemarksEditorText,
  canManageMoRemarks,
  handleSaveRemarksEditor,
  saveRemarksFullTextMutation,
  remarksEditorSubmitMode,
  remarksFieldInfo,
  showMoGenerationModal,
  setShowMoGenerationModal,
  hasMoEvidence,
  moRequestContentions,
  hasMissingMoToe,
  moGenerationPromptInput,
  setMoGenerationPromptInput,
  requestMoGenerationMutation,
  handleRequestMoGeneration,
  isMoGenerationActive,
  canStartMoGeneration,
  canToggleRequiredPerDbq,
  showRequiredFieldModal,
  handleCancelRequiredModal,
  pendingFieldStatus,
  handleApplyRequiredToTemplate,
  updateRequiredFieldsMutation,
  handleApplyRequiredToUserDbq,
  updateUserDbqRequiredFieldsMutation,
  isProvider,
  showSigningModal,
  setShowSigningModal,
  isProviderConfirmingChanges,
  providerProfiles,
  selectedProfileId,
  handleProfileSelect,
  providerProfile,
  certificationData,
  handleCertificationChange,
  providerSignActionLabel,
  handleSignSubmit,
  signDbqMutation,
  showNewlyRequiredModal,
  setShowNewlyRequiredModal,
  newlyRequiredFields,
  handleGoToRequiredField,
  showFieldConfirmation,
  cancelFieldChange,
  pendingFieldChange,
  confirmFieldChange,
}) => (
  <>
    <Modal
      show={Boolean(inlineMissingDetailSuggestion)}
      onHide={closeInlineMissingDetailModal}
      size="lg"
      scrollable
    >
      <Modal.Header closeButton>
        <Modal.Title>Complete Missing Detail</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="small text-muted mb-3">
          Fill in the missing information, then save it back to the DBQ suggestion. This does not accept the suggestion.
        </div>
        {inlineMissingDetailInputs.length > 0 ? (
          <div className="dbq-suggestion-missing-fields mb-3">
            {inlineMissingDetailInputs.map((missingInput, index) => {
              const stateKey = buildInlineMissingInputStateKey(missingInput, index);
              const inputValue = inlineMissingInputValues?.[stateKey] || '';
              const searchState = inlineMissingInputSearches?.[stateKey] || null;
              const isSearching = searchState?.status === 'searching';

              return (
                <div key={stateKey} className="dbq-suggestion-missing-field">
                  <label className="dbq-suggestion-missing-field-label">
                    {missingInput?.label || 'Missing detail'}
                  </label>
                  {missingInput?.contextPreview ? (
                    <div className="dbq-suggestion-missing-field-context">
                      {missingInput.contextPreview}
                    </div>
                  ) : null}
                  {missingInput?.helpText ? (
                    <div className="dbq-suggestion-missing-field-help">{missingInput.helpText}</div>
                  ) : null}
                  <div className="dbq-suggestion-missing-search-row">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => handleSearchInlineMissingInputValue(missingInput, index)}
                      disabled={isInlineMissingDetailSaving || isSearching}
                    >
                      {isSearching ? 'Searching...' : 'Search evidence'}
                    </Button>
                    {searchState?.message ? (
                      <div className={`dbq-suggestion-missing-search-message dbq-suggestion-missing-search-message--${searchState.status}`}>
                        {searchState.message}
                      </div>
                    ) : null}
                  </div>
                  <div className="dbq-suggestion-missing-field-actions">
                    <Form.Control
                      type="text"
                      value={inputValue}
                      onChange={(event) =>
                        handleInlineMissingInputValueChange(missingInput, index, event.target.value)
                      }
                      placeholder={missingInput?.placeholder || 'Enter missing detail'}
                      disabled={isInlineMissingDetailSaving}
                    />
                    <Button
                      variant="outline-primary"
                      onClick={() => handleApplyInlineMissingInputValue(missingInput, index)}
                      disabled={isInlineMissingDetailSaving || !String(inputValue || '').trim()}
                    >
                      Insert
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}
        <Form.Control
          as="textarea"
          rows={12}
          value={inlineMissingDetailDraftText}
          onChange={(event) => setInlineMissingDetailDraftText(event.target.value)}
          disabled={isInlineMissingDetailSaving}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={() => closeInlineMissingDetailModal()}
          disabled={isInlineMissingDetailSaving}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSaveInlineMissingDetail}
          disabled={
            isInlineMissingDetailSaving ||
            !String(inlineMissingDetailDraftText || '').trim() ||
            String(inlineMissingDetailDraftText || '').includes(MISSING_USER_INPUT_TOKEN)
          }
        >
          Save suggestion
        </Button>
      </Modal.Footer>
    </Modal>

    <Modal
      show={Boolean(inlineRejectedSuggestionsFieldId)}
      onHide={closeInlineRejectedSuggestionsModal}
      size="lg"
      scrollable
    >
      <Modal.Header closeButton>
        <Modal.Title>Rejected Suggestions</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="small text-muted mb-3">
          Rejected suggestion history for{' '}
          <strong>
            {inlineRejectedSuggestionsFieldId
              ? getFieldLabelByKey(inlineRejectedSuggestionsFieldId)
              : 'this field'}
          </strong>.
        </div>

        {inlineRejectedSuggestions.length > 0 ? (
          <div className="d-flex flex-column gap-3">
            {inlineRejectedSuggestions.map((suggestion) => (
              <div
                key={suggestion.suggestionId}
                className="dbq-suggestion-card dbq-suggestion-card--rejected mb-0"
              >
                <div className="dbq-suggestion-card-header">
                  <div className="dbq-suggestion-card-meta">
                    <span className="dbq-suggestion-card-author">
                      {getInlineSuggestionReviewerName(suggestion)}
                    </span>
                    <span className="dbq-suggestion-card-date">
                      {formatInlineSuggestionDateTime(
                        suggestion.rejectedAt || suggestion.updatedAt || suggestion.createdAt,
                      )}
                    </span>
                  </div>
                  <span className="dbq-suggestion-card-status-text dbq-suggestion-card-status-text--rejected">
                    Rejected
                  </span>
                </div>
                <div className="dbq-suggestion-card-body">
                  <div>
                    <div className="dbq-suggestion-card-section-title">Rejected suggestion</div>
                    <div style={{ whiteSpace: 'pre-wrap' }}>
                      {getInlineSuggestionDisplayText(suggestion) || 'No suggestion text captured.'}
                    </div>
                  </div>
                  {suggestion.decisionNote ? (
                    <div className="dbq-suggestion-card-note dbq-suggestion-card-decision-note">
                      <span className="dbq-suggestion-card-note-label">Decision</span>
                      {suggestion.decisionNote}
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Alert variant="secondary" className="mb-0">
            No rejected suggestions were found for this field.
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={closeInlineRejectedSuggestionsModal}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>

    <Modal
      show={Boolean(inlineEvidenceSuggestion)}
      onHide={closeInlineEvidenceModal}
      size="lg"
      scrollable
    >
      <Modal.Header closeButton>
        <Modal.Title>AI Suggestion Evidence</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="small text-muted mb-3">
          Page-level evidence used for{' '}
          <strong>{inlineEvidenceFieldId ? getFieldLabelByKey(inlineEvidenceFieldId) : 'this suggestion'}</strong>.
        </div>

        {inlineEvidenceRefs.length > 0 ? (
          <div className="dbq-suggestion-provenance-section">
            <span className="dbq-suggestion-provenance-label">Evidence pages</span>
            <div className="dbq-suggestion-evidence-list">
              {inlineEvidenceRefs.map((ref) => (
                <div key={ref.fileId} className="dbq-suggestion-evidence-item">
                  <div className="dbq-suggestion-evidence-file dbq-suggestion-evidence-file--static">
                    {ref.fileName}
                  </div>
                  <div className="dbq-suggestion-evidence-pages">
                    {ref.pages.map((pageNumber) => (
                      <button
                        key={`${ref.fileId}-${pageNumber}`}
                        type="button"
                        className="dbq-suggestion-evidence-page"
                        onClick={() => openInlineEvidencePage({
                          fileId: ref.fileId,
                          fileName: ref.fileName,
                          pageNumber,
                        })}
                      >
                        p.{pageNumber}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <Alert variant="warning" className="mb-0">
            No page-level evidence was captured for this AI suggestion.
          </Alert>
        )}

        {inlineEvidenceTranscriptRefs.length > 0 ? (
          <div className="dbq-suggestion-provenance-section mt-4">
            <span className="dbq-suggestion-provenance-label">Transcripts used</span>
            <div className="dbq-suggestion-transcript-list">
              {inlineEvidenceTranscriptRefs.map((ref) => {
                const preview = String(ref?.preview || '').trim();
                return (
                  <div
                    key={`${ref?.transcriptId || ref?.meetingDatetime || preview}`}
                    className="dbq-suggestion-transcript-item"
                  >
                    {`Transcript${ref?.meetingDatetime ? ` ${ref.meetingDatetime}` : ''}${preview ? `: ${preview}` : ''}`}
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={closeInlineEvidenceModal}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>

    <PageContentModal
      show={Boolean(inlineEvidencePage.fileId && inlineEvidencePage.pageNumber)}
      onHide={closeInlineEvidencePage}
      claimId={userDbq?.claimId}
      fileId={inlineEvidencePage.fileId}
      fileName={inlineEvidencePage.fileName}
      pageNumber={inlineEvidencePage.pageNumber}
    />

    <Modal
      show={Boolean(inlineGenerationFailureNotice)}
      onHide={closeInlineGenerationFailureModal}
    >
      <Modal.Header closeButton>
        <Modal.Title>Generation Failure</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="small text-muted mb-3">
          {inlineGenerationFailureFieldId
            ? getFieldLabelByKey(inlineGenerationFailureFieldId)
            : 'AI field'}
        </div>
        <div className="dbq-suggestion-missing-search-message dbq-suggestion-missing-search-message--error">
          {getInlineGenerationFailureMessage(inlineGenerationFailureNotice)}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={closeInlineGenerationFailureModal}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>

    {canManageMedicalOpinion && (
      <MoEvidenceModal
        show={showMoEvidenceModal}
        onHide={() => setShowMoEvidenceModal(false)}
        claimId={userDbq?.claimId}
        userDbqId={userDbq?.userDbqId || userDbqId}
        autoMoData={autoMedicalOpinionData}
      />
    )}

    {isManagedMedicalOpinionRemarks && (
      <DbqRemarksEditorModal
        show={showRemarksEditorModal}
        onHide={() => {
          setShowRemarksEditorModal(false);
          setRemarksEditorSubmitMode(null);
        }}
        value={remarksEditorText}
        onChange={setRemarksEditorText}
        onSave={canManageMoRemarks ? handleSaveRemarksEditor : undefined}
        isSaving={saveRemarksFullTextMutation.isPending}
        submitMode={remarksEditorSubmitMode}
        remarksFieldInfo={remarksFieldInfo}
        readOnly={!canManageMoRemarks}
        title={canManageMoRemarks ? 'Edit Full Medical Opinion' : 'View Full Medical Opinion'}
      />
    )}

    {canManageMedicalOpinion && (
      <Modal show={showMoGenerationModal} onHide={() => setShowMoGenerationModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{hasMoEvidence ? 'Regenerate Medical Opinion' : 'Generate Medical Opinion'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <div className="fw-semibold mb-2" style={{ fontSize: '0.92rem' }}>
              Contention / TOE pairs
            </div>
            {moRequestContentions.length === 0 ? (
              <Alert variant="warning" className="mb-0">
                This DBQ request does not have any saved contentions yet. Update the request workspace first.
              </Alert>
            ) : (
              <>
                <div className="d-flex flex-column gap-2">
                  {moRequestContentions.map((requestContention) => (
                    <div
                      key={`${requestContention.contention}-${requestContention.sortOrder}`}
                      className="border rounded p-3"
                      style={{ background: '#f8f9fb' }}
                    >
                      <div className="fw-semibold" style={{ fontSize: '0.9rem' }}>
                        {requestContention.contentionLabel}
                      </div>
                      <div className="text-muted small">
                        TOE: {requestContention.toeLabel}
                      </div>
                    </div>
                  ))}
                </div>
                <Form.Text className="text-muted">
                  Generation will create one Medical Opinion per contention and TOE pair, then combine them into the DBQ remarks and PDF.
                </Form.Text>
              </>
            )}
            {hasMissingMoToe ? (
              <Alert variant="warning" className="mt-3 mb-0">
                Every contention needs a TOE before generation can start.
              </Alert>
            ) : null}
          </div>

          <Form.Group>
            <Form.Label>Optional instructions</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              placeholder="Example: tighten rationale and focus on aggravation evidence."
              value={moGenerationPromptInput}
              onChange={(e) => setMoGenerationPromptInput(e.target.value)}
              disabled={requestMoGenerationMutation.isPending}
            />
            <Form.Text className="text-muted">
              Leave blank to use the default generation prompt.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowMoGenerationModal(false)}
            disabled={requestMoGenerationMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleRequestMoGeneration}
            disabled={isMoGenerationActive || requestMoGenerationMutation.isPending || !canStartMoGeneration}
          >
            {requestMoGenerationMutation.isPending ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                Starting...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faLightbulb} className="me-2" />
                {hasMoEvidence ? 'Regenerate MO' : 'Generate MO'}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    )}

    {canToggleRequiredPerDbq && (
      <Modal show={showRequiredFieldModal} onHide={handleCancelRequiredModal}>
        <Modal.Header closeButton>
          <Modal.Title>
            {pendingFieldStatus.isRequired ? 'Remove Required Status' : 'Mark Field as Required'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {pendingFieldStatus.isRequired ? (
            <>
              <p>This field is currently marked as required{pendingFieldStatus.level === 'template' ? ' for all DBQs of this type' : ' for this DBQ only'}.</p>
              <p>Where would you like to remove the required status from?</p>

              {pendingFieldStatus.isInTemplate && (
                <div className="d-grid gap-2 mb-3">
                  <Button
                    variant="outline-danger"
                    onClick={handleApplyRequiredToTemplate}
                    disabled={updateRequiredFieldsMutation.isPending}
                  >
                    {updateRequiredFieldsMutation.isPending ? (
                      <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                    ) : null}
                    Remove from all DBQs of this type
                  </Button>
                </div>
              )}

              {pendingFieldStatus.isInUser && (
                <div className="d-grid gap-2">
                  <Button
                    variant="outline-warning"
                    onClick={handleApplyRequiredToUserDbq}
                    disabled={updateUserDbqRequiredFieldsMutation.isPending}
                  >
                    {updateUserDbqRequiredFieldsMutation.isPending ? (
                      <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                    ) : null}
                    Remove from this DBQ only
                  </Button>
                </div>
              )}
            </>
          ) : (
            <>
              <p>Where would you like to apply the required status?</p>

              <div className="d-grid gap-2 mb-3">
                <Button
                  variant="outline-primary"
                  onClick={handleApplyRequiredToUserDbq}
                  disabled={updateUserDbqRequiredFieldsMutation.isPending}
                >
                  {updateUserDbqRequiredFieldsMutation.isPending ? (
                    <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                  ) : null}
                  This DBQ only
                  <span className="d-block small text-muted mt-1">Only affects this specific user's form</span>
                </Button>
              </div>

              <div className="d-grid gap-2">
                <Button
                  variant="primary"
                  onClick={handleApplyRequiredToTemplate}
                  disabled={updateRequiredFieldsMutation.isPending}
                >
                  {updateRequiredFieldsMutation.isPending ? (
                    <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                  ) : null}
                  All DBQs of this type
                  <span className="d-block small mt-1">Affects all current and future DBQs using this template</span>
                </Button>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelRequiredModal}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    )}

    {isProvider && (
      <Modal show={showSigningModal} onHide={() => setShowSigningModal(false)} size="lg">
        <Modal.Header closeButton style={{ background: '#f8f9fa', borderBottom: '2px solid #4caf50' }}>
          <Modal.Title>
            <FontAwesomeIcon icon={faSignature} className="me-2 text-success" />
            {isProviderConfirmingChanges ? 'Confirm DBQ Changes' : 'Sign & Submit DBQ'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info" className="mb-4" style={{ border: 'none' }}>
            <strong>Certification Statement:</strong>
            <p className="mb-0 mt-2">
              To the best of my knowledge, the information contained herein is accurate, complete and current.
            </p>
          </Alert>

          {providerProfiles.length === 0 && (
            <Alert variant="warning" className="mb-4" style={{ border: 'none' }}>
              <FontAwesomeIcon icon={faLightbulb} className="me-2" />
              You can set up a certification profile under <strong>My Profile</strong> for your Examiner Details.
            </Alert>
          )}

          <div
            style={{
              background: 'linear-gradient(135deg, #fcfcfd 0%, #f5f6f8 100%)',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '24px',
              border: '1px solid rgba(0, 0, 0, 0.08)',
            }}
          >
            <Row>
              <Col md={6}>
                <Form.Label className="fw-semibold mb-3" style={{ color: '#495057' }}>
                  Certification Profile
                </Form.Label>
                <div className="d-flex flex-column" style={{ gap: '8px', height: '152px' }}>
                  {[0, 1, 2].map((index) => {
                    const profile = providerProfiles[index];
                    if (profile) {
                      const isSelected = selectedProfileId === profile.profileId;
                      return (
                        <button
                          key={profile.profileId}
                          type="button"
                          onClick={() => handleProfileSelect(profile.profileId)}
                          style={{
                            flex: 1,
                            padding: '0 16px',
                            borderRadius: '8px',
                            border: isSelected
                              ? '1px solid #4caf50'
                              : '1px solid rgba(0, 0, 0, 0.2)',
                            background: isSelected
                              ? '#fff'
                              : 'rgba(0, 0, 0, 0.06)',
                            boxShadow: isSelected
                              ? 'inset 0 0 0 1px #4caf50, 0 2px 8px rgba(76, 175, 80, 0.2)'
                              : 'none',
                            textAlign: 'left',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                        >
                          <span className="fw-medium">{profile.profileName}</span>
                          {isSelected && (
                            <FontAwesomeIcon icon={faCheckCircle} className="text-success" />
                          )}
                        </button>
                      );
                    }
                    return (
                      <div
                        key={`empty-${index}`}
                        style={{
                          flex: 1,
                          padding: '0 16px',
                          borderRadius: '8px',
                          border: '1px dashed rgba(0, 0, 0, 0.15)',
                          background: 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <span className="text-muted fst-italic" style={{ fontSize: '0.9rem' }}>
                          Profile {index + 1} not configured
                        </span>
                      </div>
                    );
                  })}
                </div>
              </Col>

              <Col md={6}>
                <Form.Label className="fw-semibold mb-3" style={{ color: '#495057' }}>
                  Your Signature
                </Form.Label>
                <div
                  style={{
                    background: '#fff',
                    borderRadius: '8px',
                    padding: '20px',
                    height: '152px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid rgba(0, 0, 0, 0.1)',
                  }}
                >
                  {providerProfile?.providerSignatureUrl ? (
                    <Image
                      src={providerProfile.providerSignatureUrl}
                      alt="Signature"
                      style={{ maxWidth: '100%', maxHeight: '120px' }}
                    />
                  ) : (
                    <span className="text-muted">No signature on file</span>
                  )}
                </div>
              </Col>
            </Row>
          </div>

          <div
            className="d-flex align-items-center mb-4"
            style={{
              borderBottom: '1px solid #dee2e6',
              paddingBottom: '12px',
            }}
          >
            <FontAwesomeIcon icon={faSignature} className="me-2 text-success" />
            <span className="fw-semibold" style={{ color: '#495057' }}>
              Examiner Details
            </span>
          </div>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Examiner's Signature (Name)</Form.Label>
                <Form.Control
                  name="signature"
                  value={certificationData.signature}
                  onChange={handleCertificationChange}
                  placeholder="Your name"
                  style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '6px' }}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Date Signed</Form.Label>
                <Form.Control
                  type="date"
                  name="dateSigned"
                  value={certificationData.dateSigned}
                  onChange={handleCertificationChange}
                  style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '6px' }}
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Examiner's Printed Name and Title <span className={certificationData.nameAndTitle ? 'text-muted' : 'text-danger'}>*</span></Form.Label>
            <Form.Control
              name="nameAndTitle"
              value={certificationData.nameAndTitle}
              onChange={handleCertificationChange}
              placeholder="e.g., John Doe, MD"
              style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '6px' }}
            />
            <Form.Text className="text-muted">
              e.g., MD, DO, DDS, DMD, Ph.D, Psy.D, NP, PA-C
            </Form.Text>
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Area of Practice/Specialty</Form.Label>
                <Form.Control
                  name="specialty"
                  value={certificationData.specialty}
                  onChange={handleCertificationChange}
                  placeholder="e.g., Psychiatry"
                  style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '6px' }}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Phone/Fax Numbers</Form.Label>
                <Form.Control
                  name="phoneOrFax"
                  value={certificationData.phoneOrFax}
                  onChange={handleCertificationChange}
                  placeholder="(555) 123-4567"
                  style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '6px' }}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>National Provider Identifier (NPI) <span className={certificationData.npi ? 'text-muted' : 'text-danger'}>*</span></Form.Label>
                <Form.Control
                  name="npi"
                  value={certificationData.npi}
                  onChange={handleCertificationChange}
                  placeholder="NPI Number"
                  style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '6px' }}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Medical License Number and State <span className={certificationData.licenseNumberAndState ? 'text-muted' : 'text-danger'}>*</span></Form.Label>
                <Form.Control
                  name="licenseNumberAndState"
                  value={certificationData.licenseNumberAndState}
                  onChange={handleCertificationChange}
                  placeholder="License # — State"
                  style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '6px' }}
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Examiner's Address</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              name="address"
              value={certificationData.address}
              onChange={handleCertificationChange}
              placeholder="Full business address"
              style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '6px' }}
            />
          </Form.Group>

          {(!certificationData.nameAndTitle || !certificationData.npi || !certificationData.licenseNumberAndState) && (
            <p className="mt-3 mb-0 text-center" style={{ color: '#dc3545' }}>
              Complete all required examiner fields to {providerSignActionLabel}.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowSigningModal(false)}
            style={{ borderRadius: '8px', padding: '10px 24px' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSignSubmit}
            disabled={signDbqMutation.isPending || !certificationData.nameAndTitle || !certificationData.npi || !certificationData.licenseNumberAndState}
            style={{
              background: 'linear-gradient(135deg, #4caf50 0%, #43a047 100%)',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 24px',
              fontWeight: 500,
              boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)',
            }}
          >
            {signDbqMutation.isPending ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                {isProviderConfirmingChanges ? 'Confirming...' : 'Signing...'}
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faFileSignature} className="me-2" />
                {providerSignActionLabel}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    )}

    <Modal show={showNewlyRequiredModal} onHide={() => setShowNewlyRequiredModal(false)}>
      <Modal.Header closeButton style={{ background: '#fff3cd', borderBottom: '2px solid #ffc107' }}>
        <Modal.Title>
          <span className="text-warning me-2">&#9888;</span>
          Field Now Required
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>Based on your selection, the following field{newlyRequiredFields.length > 1 ? 's are' : ' is'} now required:</p>
        <ul className="mb-0">
          {newlyRequiredFields.map((field, idx) => (
            <li key={`${field.fieldId}-${field.optionId || ''}-${idx}`}>
              <strong>{field.optionLabel || field.fieldLabel}</strong>
              {field.optionLabel && (
                <span className="text-muted small ms-1">({field.fieldLabel})</span>
              )}
            </li>
          ))}
        </ul>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowNewlyRequiredModal(false)}>
          OK
        </Button>
        {newlyRequiredFields.length === 1 && (
          <Button variant="primary" onClick={() => handleGoToRequiredField(newlyRequiredFields[0])}>
            Go to Field
          </Button>
        )}
        {newlyRequiredFields.length > 1 && (
          <Button variant="primary" onClick={() => handleGoToRequiredField(newlyRequiredFields[0])}>
            Go to First Field
          </Button>
        )}
      </Modal.Footer>
    </Modal>

    <Modal show={showFieldConfirmation} onHide={cancelFieldChange} centered>
      <Modal.Header closeButton>
        <Modal.Title>Confirm Selection</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="warning" className="mb-0">
          {pendingFieldChange?.message}
        </Alert>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={cancelFieldChange}>
          Cancel
        </Button>
        <Button variant="primary" onClick={confirmFieldChange}>
          Confirm
        </Button>
      </Modal.Footer>
    </Modal>
  </>
);

export default UserDbqEditorRequestFlowDialogs;
